import React, { useState, useEffect, useRef } from 'react';
import { motion, useMotionValue, useTransform, animate } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { soundEffects } from '../audio/SoundEffects';

const MENU_ITEMS = [
    { label: 'ACCUEIL', path: '/' },
    { label: 'BLOG', path: '/blog' },
    { label: 'SUIVRE MA COMMANDE', path: '/track' },
    { label: 'ESPACE PRO', path: '/custom' },
    { label: 'CONTACT', path: '/contact' },
];

const ITEM_HEIGHT = 60;

const MobileZoomMenu = ({ isOpen, onClose }) => {
    const navigate = useNavigate();
    const containerRef = useRef(null);

    const y = useMotionValue(0);
    const [activeIndex, setActiveIndex] = useState(0);

    useEffect(() => {
        if (isOpen) {
            y.set(0);
            setActiveIndex(0);
        }
    }, [isOpen, y]);

    // Calculer l'index actif en temps réel
    useEffect(() => {
        const unsubscribe = y.on("change", (latestY) => {
            const rawIndex = -latestY / ITEM_HEIGHT;
            let index = Math.round(rawIndex);

            if (index < 0) index = 0;
            if (index > MENU_ITEMS.length - 1) index = MENU_ITEMS.length - 1;

            if (index !== activeIndex) {
                soundEffects.play('hover');
                if (navigator.vibrate) navigator.vibrate(5);
                setActiveIndex(index);
            }
        });
        return () => unsubscribe();
    }, [activeIndex, y]);

    const handleDragEnd = () => {
        // Snap to closest item
        const targetY = -activeIndex * ITEM_HEIGHT;

        animate(y, targetY, {
            type: "spring",
            stiffness: 300,
            damping: 30,
            onComplete: () => {
                // Navigate automatically when snap animation completes
                soundEffects.play('click');
                const item = MENU_ITEMS[activeIndex];
                if (item) {
                    navigate(item.path);
                    onClose();
                }
            }
        });
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] flex justify-end animate-in fade-in duration-200">
            {/* Backdrop Blur */}
            <div className="absolute inset-0 bg-black/60 backdrop-blur-md" onClick={onClose} />

            {/* Dial interface area */}
            <div
                className="relative w-full max-w-sm h-full bg-gradient-to-l from-black via-black/90 to-transparent flex items-center justify-end overflow-hidden"
                ref={containerRef}
            >
                {/* The Draggable Area */}
                <motion.div
                    className="absolute inset-0 z-30 cursor-grab active:cursor-grabbing"
                    drag="y"
                    dragConstraints={{ top: -(MENU_ITEMS.length - 1) * ITEM_HEIGHT, bottom: 0 }}
                    style={{ y }}
                    onDragEnd={handleDragEnd}
                    dragElastic={0.2}
                />

                {/* The "Wheel" Visuals */}
                <div className="relative h-full w-full flex flex-col items-end justify-center pointer-events-none pr-8 perspective-[1000px]">
                    {MENU_ITEMS.map((item, i) => (
                        <DialItem
                            key={i}
                            item={item}
                            index={i}
                            parentY={y}
                            isActive={i === activeIndex}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

// Sub-component for individual items with 3D transform
const DialItem = ({ item, index, parentY, isActive }) => {
    const yOffset = useTransform(parentY, (value) => {
        const currentCenter = -value / ITEM_HEIGHT;
        const dist = index - currentCenter;
        return dist * 50;
    });

    const rotateX = useTransform(parentY, (value) => {
        const currentCenter = -value / ITEM_HEIGHT;
        const dist = index - currentCenter;
        return -dist * 15;
    });

    const opacity = useTransform(parentY, (value) => {
        const currentCenter = -value / ITEM_HEIGHT;
        const dist = Math.abs(index - currentCenter);
        if (dist > 4) return 0;
        return 1 - (dist * 0.25);
    });

    const scale = useTransform(parentY, (value) => {
        const currentCenter = -value / ITEM_HEIGHT;
        const dist = Math.abs(index - currentCenter);
        return Math.max(0.6, 1 - (dist * 0.15));
    });

    const color = isActive ? '#ffffff' : '#666666';

    return (
        <motion.div
            style={{
                y: yOffset,
                rotateX: rotateX,
                opacity,
                scale,
                color,
                position: 'absolute',
                right: '2rem',
                transformOrigin: 'right center',
            }}
            className={`text-right flex items-center gap-4 h-[60px] whitespace-nowrap origin-right ${isActive ? 'font-black text-2xl' : 'font-medium text-lg'}`}
        >
            <span className="uppercase tracking-widest">{item.label}</span>
        </motion.div>
    );
};

export default MobileZoomMenu;
